/**
 * What a conversation looked like when it was last on screen (or prefetched): its detail plus its event log.
 *
 * Opening a cached conversation paints it at once from this and then only asks the server for what changed
 * (the detail, and events after the last one we have). Home prefetches its first conversation into it so the
 * first open is instant too. Module-level, so it outlives the surfaces that show it; a small LRU.
 * Each fetch also warms the records its cards show (recordCache.ts), so they paint with it rather than after it.
 */
import { agentApi, type SessionDetail } from './api'
import type { AnyAgentEvent } from './protocol/types'
import { clearRecordCache, warmRecords } from './recordCache'

export interface CachedSession {
    detail: SessionDetail
    events: AnyAgentEvent[]
}

const MAX_ENTRIES = 12
/** A page of the event log; a shorter page is the last one. */
const PAGE = 500

const entries = new Map<string, CachedSession>()
const inflight = new Map<string, Promise<CachedSession | null>>()

function touch(id: string, entry: CachedSession): void {
    entries.delete(id)
    entries.set(id, entry)
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value as string)
}

/** The last event id we have for this conversation (0: none). */
export function lastEventId(entry: CachedSession): number {
    return entry.events.length ? entry.events[entry.events.length - 1].id : 0
}

/** Events after `after`, every page. */
export async function fetchEventsAfter(id: string, after: number): Promise<AnyAgentEvent[]> {
    const api = agentApi()
    const out: AnyAgentEvent[] = []
    for (;;) {
        const page = (await api.pollEvents(id, after)).events as AnyAgentEvent[]
        out.push(...page)
        if (page.length < PAGE) return out
        after = page[page.length - 1].id
    }
}

/** The whole conversation from the server (detail and log fetched side by side), stored in the cache. */
export function fetchSession(id: string): Promise<CachedSession | null> {
    const running = inflight.get(id)
    if (running) return running
    const p = Promise.all([agentApi().getSession(id), fetchEventsAfter(id, 0)])
        .then(([detail, events]) => {
            const entry = { detail, events }
            touch(id, entry)
            void warmRecords(id, events)
            return entry
        })
        .finally(() => inflight.delete(id))
    inflight.set(id, p)
    return p
}

export function getCachedSession(id: string): CachedSession | null {
    const entry = entries.get(id)
    if (entry) touch(id, entry)
    return entry ?? null
}

/** A fetch already under way for this conversation (a prefetch the open can wait on instead of starting over). */
export function inflightSession(id: string): Promise<CachedSession | null> | null {
    return inflight.get(id) ?? null
}

/** Warm the cache in the background; failures are ignored (the open just loads normally). */
export function prefetchSession(id: string | null | undefined): void {
    if (!id || entries.has(id) || inflight.has(id)) return
    fetchSession(id).catch(() => { /* the open loads it normally */ })
}

/** Keep the cached log current as events arrive (replayed or live), so the next open has them. */
export function appendCachedEvent(id: string | null, ev: AnyAgentEvent): void {
    if (!id) return
    const entry = entries.get(id)
    // A copy: the reducer may keep (and later change) objects from the event it is given.
    if (entry && ev.id > lastEventId(entry)) entry.events.push(structuredClone(ev))
}

export function updateCachedDetail(id: string, detail: SessionDetail): void {
    const entry = entries.get(id)
    if (entry) entry.detail = detail
}

export function clearSessionCache(): void {
    entries.clear()
    inflight.clear()
    clearRecordCache()
}
